/* v18 (2026-07-04): Two-tier reward milestone bar.
   Single rail, two milestones:
     T1 (default ₹999)  → free shipping + 1 free sample  (truck node)
     T2 (default ₹2000) → 3 free samples                 (gift node)
   Fill width = progress across the whole 0→T2 range. Nodes flip to
   "unlocked" as the cart crosses each threshold. Thresholds come from
   window.mom_samples (exposed by global-script.liquid); falls back to the
   legacy window.free_shipping_price for T1.

   Re-render: the bar lives in the drawer SHELL (never re-swapped), so besides
   the legacy `change` event it also watches `.previewCart-wrapper` via a
   MutationObserver — every cart mutation (add / qty / remove / coupon) rebuilds
   that body and thus re-runs initialize(). Decoupled from any single event name. */

class FreeShippingMeter extends HTMLElement {
    constructor() {
        super();
    }

    // ── Message copy (single edit point). {money} is replaced with the formatted remaining amount.
    static MSG = {
        empty:  'Spend {t1} to unlock free shipping',
        toT1:   'Add {money} more for free shipping',
        atT1:   'Free shipping unlocked',
        atT2:   'Free shipping unlocked'
    };

    connectedCallback() {
        this.progressBar   = this.querySelector('[data-shipping-progress]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
        this.node1 = this.querySelector('[data-milestone="1"]');
        this.node2 = this.querySelector('[data-milestone="2"]');

        // Thresholds in paise (cart.total_price is in paise). window.mom_samples is rupees.
        const s = window.mom_samples || {};
        const legacyT1 = parseInt(window.free_shipping_price || 0);      // already paise
        this.t1 = s.t1 ? Math.round(s.t1 * 100) : (legacyT1 || 99900);
        this.t2 = s.t2 ? Math.round(s.t2 * 100) : 200000;
        if (this.t2 <= this.t1) this.t2 = this.t1 * 2;

        this.addEventListener('change', this.onCartChange.bind(this));
        this.observeCart();
        this.initialize();
    }

    // Watch the AJAX-swapped body so every cart change refreshes the bar.
    observeCart() {
        if (this._observer) return;
        const wrap = document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
        if (!wrap) return;
        let raf = null;
        this._observer = new MutationObserver(() => {
            if (raf) return;
            raf = requestAnimationFrame(() => { raf = null; this.initialize(); });
        });
        this._observer.observe(wrap, { childList: true, subtree: true });
    }

    onCartChange() { this.initialize(); }

    initialize() {
        if (typeof Shopify === 'undefined' || !Shopify.getCart) return;
        Shopify.getCart((cart) => this.render(cart));
    }

    render(cart) {
        if (!this.progressMeter || !this.messageElement) return;
        let totalPrice = cart.total_price; // paise

        // Legacy: exclude gift-card items when the store is in that mode.
        if (typeof $ !== 'undefined' && $('body').hasClass('setup_shipping_delivery')) {
            const giftCardItems = $('.cart-item[data-price-gift-card], .previewCartItem[data-price-gift-card]');
            giftCardItems.each(function () {
                totalPrice -= parseFloat($(this).attr('data-price-gift-card'));
            });
        }
        totalPrice = parseInt(totalPrice) || 0;

        // Single free-shipping tier: fill spans 0→T1 (100% at the free-shipping threshold).
        let fill = Math.min(100, Math.max(0, (totalPrice / this.t1) * 100));

        const reachedT1 = totalPrice >= this.t1;
        const reachedT2 = totalPrice >= this.t2;

        // Empty cart → hide the rail, show a prompt.
        const isEmpty = (cart.item_count === 0) || totalPrice === 0;
        this.progressBar.classList.toggle('progress-hidden', isEmpty);

        let text;
        if (isEmpty) {
            text = FreeShippingMeter.MSG.empty.replace('{t1}', this.money(this.t1));
        } else if (reachedT2) {
            text = FreeShippingMeter.MSG.atT2;
        } else if (reachedT1) {
            text = FreeShippingMeter.MSG.atT1.replace('{money}', this.money(this.t2 - totalPrice));
        } else {
            text = FreeShippingMeter.MSG.toT1.replace('{money}', this.money(this.t1 - totalPrice));
        }

        // Node unlocked states.
        if (this.node1) this.node1.classList.toggle('is-unlocked', reachedT1);
        if (this.node2) this.node2.classList.toggle('is-unlocked', reachedT2);
        this.progressBar.classList.toggle('at-t1', reachedT1 && !reachedT2);
        this.progressBar.classList.toggle('at-t2', reachedT2);

        // Single-tier: the T1 node sits at the end of the rail (positioned by CSS --end).

        setTimeout(() => {
            this.progressMeter.style.width = fill.toFixed(2) + '%';
            this.messageElement.innerHTML = '<span>' + text + '</span>';
        }, 60);
    }

    money(paise) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            return Shopify.formatMoney(paise, window.money_format);
        }
        return '₹' + Math.round(paise / 100);
    }
}

window.addEventListener('load', () => {
    if (!customElements.get('free-shipping-component')) {
        customElements.define('free-shipping-component', FreeShippingMeter);
    }
});
